/**
 * One source of truth for search and generative engine metadata.
 *
 * Classic SEO and GEO are served by the same discipline: a single accurate
 * description per page, one canonical URL, and structured data that states the
 * facts an engine would otherwise have to infer. Every string here is written to
 * be quoted verbatim by an answer engine, so each one leads with the answer.
 */

export const SITE = {
  name: 'Cavalla Solution',
  /** The live domain. Change here and every canonical, Open Graph and schema URL follows. */
  url: 'https://cavalladata.com',
  ethos: 'Made for people, powered by AI.',
  description:
    'Cavalla Solution is a consulting firm in market intelligence, data led growth and augmented intelligence, serving African and global organisations.',
  founder: 'Jonathan Clarke',
  locale: 'en_GB',
  /** Used as the Open Graph image where a page has no image of its own. */
  shareImage: 'https://cdn.magicpatterns.com/uploads/nTjBqcuuXgbjSwDzeQVhBn/Cavalla.png',
  /** The company LinkedIn page. Also declared as sameAs, so engines tie the two together. */
  linkedin: 'https://www.linkedin.com/company/cavalla-solution',
  email: 'hello@cavalladata.com',
} as const

/** The one LinkedIn URL used everywhere it appears. */
export const LINKEDIN_URL = SITE.linkedin
export const CONTACT_EMAIL = SITE.email

/** An absolute, canonical URL for a route. Paths are given with a leading slash. */
export function canonical(path: string): string {
  if (path === '/') return `${SITE.url}/`
  return `${SITE.url}${path.replace(/\/$/, '')}`
}

export interface Breadcrumb {
  name: string
  path: string
}

/** BreadcrumbList for a nested page. Pass the trail including the current page. */
export function breadcrumbSchema(trail: Breadcrumb[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: canonical(crumb.path),
    })),
  }
}

/**
 * The organisation itself. Carried on every page, because answer engines cite
 * the entity rather than the page, and a consistent entity across the site is
 * what lets them do it.
 */
export function organisationSchema() {
  return {
    '@type': 'Organization',
    '@id': `${SITE.url}/#organisation`,
    name: SITE.name,
    url: SITE.url,
    logo: SITE.shareImage,
    description: SITE.description,
    slogan: SITE.ethos,
    founder: { '@type': 'Person', name: SITE.founder },
    sameAs: [SITE.linkedin],
    areaServed: [
      { '@type': 'Place', name: 'Africa' },
      { '@type': 'Place', name: 'Worldwide' },
    ],
    knowsAbout: [
      'Market intelligence',
      'Augmented intelligence',
      'Artificial intelligence readiness',
      'Go to market strategy',
      'National development plan delivery',
      'Development finance',
      'Data governance',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Sales and enquiries',
      email: SITE.email,
      availableLanguage: ['English'],
    },
  }
}

export function webSiteSchema() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE.url}/#website`,
    name: SITE.name,
    url: SITE.url,
    inLanguage: 'en-GB',
    publisher: { '@id': `${SITE.url}/#organisation` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE.url}/insights?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}

/** A service line or the platform, as a Service offered by the organisation. */
export function serviceSchema(input: {
  name: string
  description: string
  path: string
  serviceType: string
  audience?: string
  offers?: string[]
}) {
  return {
    '@type': 'Service',
    '@id': `${canonical(input.path)}#service`,
    name: input.name,
    description: input.description,
    serviceType: input.serviceType,
    url: canonical(input.path),
    provider: { '@id': `${SITE.url}/#organisation` },
    areaServed: [
      { '@type': 'Place', name: 'Africa' },
      { '@type': 'Place', name: 'Worldwide' },
    ],
    ...(input.audience
      ? { audience: { '@type': 'Audience', audienceType: input.audience } }
      : {}),
    ...(input.offers && input.offers.length > 0
      ? {
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: `${input.name} engagements`,
            itemListElement: input.offers.map((offer) => ({
              '@type': 'Offer',
              itemOffered: { '@type': 'Service', name: offer },
            })),
          },
        }
      : {}),
  }
}

/** An article, with the full body length declared so engines can weigh it. */
export function articleSchema(input: {
  title: string
  description: string
  path: string
  image: string
  publishedAt: string
  author: { name: string; role: string }
  section?: string
  about: string[]
  wordCount: number
  readTime: number
  summary: string
}) {
  return {
    '@type': 'Article',
    '@id': `${canonical(input.path)}#article`,
    headline: input.title,
    description: input.description,
    abstract: input.summary,
    url: canonical(input.path),
    image: input.image,
    datePublished: input.publishedAt,
    dateModified: input.publishedAt,
    inLanguage: 'en-GB',
    wordCount: input.wordCount,
    timeRequired: `PT${input.readTime}M`,
    author: {
      '@type': 'Person',
      name: input.author.name,
      jobTitle: input.author.role,
      worksFor: { '@id': `${SITE.url}/#organisation` },
    },
    publisher: { '@id': `${SITE.url}/#organisation` },
    ...(input.section ? { articleSection: input.section } : {}),
    about: input.about.map((topic) => ({ '@type': 'Thing', name: topic })),
    isAccessibleForFree: true,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical(input.path) },
  }
}

/** FAQPage, built from a visible question and answer set. */
export function faqSchema(items: { question: string; answer: string }[]) {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }
}

/** A downloadable resource, declared as a free lead magnet. */
export function resourceSchema(input: {
  title: string
  description: string
  path: string
  image: string
}) {
  return {
    '@type': 'Book',
    '@id': `${canonical(input.path)}#resource`,
    name: input.title,
    description: input.description,
    url: canonical(input.path),
    image: input.image,
    bookFormat: 'https://schema.org/EBook',
    inLanguage: 'en-GB',
    author: { '@id': `${SITE.url}/#organisation` },
    publisher: { '@id': `${SITE.url}/#organisation` },
    isAccessibleForFree: true,
  }
}

/** The demonstration video on the Cavalla Gov page. */
export function videoSchema(input: {
  name: string
  description: string
  contentUrl: string
  thumbnailUrl: string
  path: string
}) {
  return {
    '@type': 'VideoObject',
    name: input.name,
    description: input.description,
    contentUrl: input.contentUrl,
    thumbnailUrl: input.thumbnailUrl,
    embedUrl: canonical(input.path),
    uploadDate: '2026-09-01',
    publisher: { '@id': `${SITE.url}/#organisation` },
  }
}
